/**
 * Single source of truth for Legacy RTL volume / delta / trades chart row filtering.
 * Keeps category, underlying, expiry, side, moneyness, and data-mode field presence aligned across tools.
 */
import { ScannerData } from '../types/ScannerData';
import { formatDate } from './dateUtils';
import { calculateMoneynessCategory } from './moneynessUtils';
import { getUnderlyingSymbol } from './filterUtils';
import { getMappedSymbol } from './optionsUtils';
import { matchesCategoryFilter } from './symbolCategoryUtils';
import { DataMode } from '../types/DataMode';

export type LegacyVolumeCategory = 'all' | 'stocks' | 'etfs' | 'indices';

export interface LegacyVolumeFilterParams {
  selectedCategory: LegacyVolumeCategory;
  selectedUnderlying: string;
  selectedExpiry: string;
  selectedSide: string;
  selectedMoneyness: string;
  selectedDataMode: DataMode;
  isSymbolMappingEnabled: boolean;
}

function fieldPresent(record: ScannerData, key: string): boolean {
  const o = record as unknown as Record<string, unknown>;
  return o[key] !== undefined;
}

function hasAccumulatedShape(record: ScannerData): boolean {
  return (
    fieldPresent(record, 'Bid Volume accumelated') ||
    fieldPresent(record, 'Ask Volume accumelated') ||
    fieldPresent(record, 'Bid Count accumelated') ||
    fieldPresent(record, 'Ask Count accumelated') ||
    fieldPresent(record, 'Trades accumelated') ||
    fieldPresent(record, 'Delta accumelated') ||
    fieldPresent(record, 'Direction accumelated')
  );
}

function hasFiveMinuteShape(record: ScannerData): boolean {
  return (
    fieldPresent(record, 'Bid Volume') ||
    fieldPresent(record, 'Ask Volume') ||
    fieldPresent(record, 'Bid Count') ||
    fieldPresent(record, 'Ask Count') ||
    fieldPresent(record, 'Trade Count') ||
    fieldPresent(record, 'Delta ( ask - bid )') ||
    fieldPresent(record, 'Direction')
  );
}

/**
 * Filter raw scanner rows the same way Volume Delta Reformed did internally (now shared).
 */
export function filterLegacyVolumeScannerData(
  data: ScannerData[],
  p: LegacyVolumeFilterParams
): ScannerData[] {
  if (!data?.length) return [];

  const standardFiltered = data.filter((item) => {
    const underlying = getUnderlyingSymbol(item);
    if (!matchesCategoryFilter(underlying, p.selectedCategory)) {
      return false;
    }
    const expiry = item['Expiry Date'] || 'Unknown';
    const rawSide = String(item['Option Type'] || 'Unknown').toUpperCase();
    const side = rawSide === 'CALL' ? 'C' : rawSide === 'PUT' ? 'P' : rawSide;
    const moneyness = calculateMoneynessCategory(item.Moneyness, side as 'C' | 'P' | string);

    const mappedItemUnderlying = getMappedSymbol(underlying, p.isSymbolMappingEnabled);
    const mappedSelectedUnderlying = getMappedSymbol(p.selectedUnderlying, p.isSymbolMappingEnabled);
    const underlyingMatch =
      p.selectedUnderlying === 'all' || mappedItemUnderlying === mappedSelectedUnderlying;

    const formattedExpiry = formatDate(expiry);
    const expiryMatch = p.selectedExpiry === 'all' || formattedExpiry === p.selectedExpiry;

    const sideMatch = p.selectedSide === 'all' || side === String(p.selectedSide).toUpperCase();
    const moneynessMatch = p.selectedMoneyness === 'all' || moneyness === p.selectedMoneyness;

    return underlyingMatch && expiryMatch && sideMatch && moneynessMatch;
  });

  let dataModeFiltered: ScannerData[];
  if (p.selectedDataMode === 'accumulated') {
    dataModeFiltered = standardFiltered.filter(
      (record) => hasAccumulatedShape(record) || standardFiltered.length === 0
    );
  } else {
    dataModeFiltered = standardFiltered.filter(
      (record) => hasFiveMinuteShape(record) || standardFiltered.length === 0
    );
  }

  if (dataModeFiltered.length === 0 && standardFiltered.length > 0) {
    dataModeFiltered = standardFiltered;
  }

  return dataModeFiltered;
}
